import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import { prefetchIndexes } from "@/services/configApi";
import { firebaseReady, getAdminMode, isAdministrator } from "@/providers/firebase/authProvider";
// project123 D-002: imported for its side effect — starts the app-wide PostHog base
// lane (cookieless) and honors an existing consent cookie (full lane). Mounted here
// (not just PublicLayout) so the logged-in app is tracked too, not only public pages.
import "@/config/posthog";
// The DEV FE is redeployed on every push, and a tab that still holds the previous entry asks
// for a chunk that no longer exists — the SPA rewrite answers it with HTML, the module load
// fails and the app lands on its error boundary. This turns that into one reload. See the
// file header for the verified evidence and why the window cannot loop.
import { installDeployHandoverReload } from "@/lib/deployHandover";

installDeployHandoverReload();

/**
 * ⚠️ THE DIMENSION INDEXES ARE ADMIN DATA, SO THEY ARE NO LONGER WARMED FOR EVERYONE
 * (P206 T0400 — found by auditing the settings page's own network traffic).
 *
 * This was an unconditional `prefetchIndexes()` on every app start, for every session, in every
 * shell: the entire taxonomy (BD/KD/PD/ED) was requested *before React even mounted*, so it could
 * not be conditional on the mode the person is in. That is the PO's original report — *"i see alot
 * of indexes. Why would that be in there for a user?"* — at the layer he could not see: the surface
 * had been gated behind admin mode (T0200/T0201), and the TRANSPORT had not.
 *
 * The warm-up still happens for an admin in admin mode, which is who opens the taxonomy surfaces.
 * ⚠️ NOTHING IS BROKEN FOR A USER WHO NEEDS AN INDEX: `useIndexDimensions()` (the entity onboarding
 * wizard) and `fetchIndexes()` (the hierarchy view) are cache-first and fetch on demand, so they
 * read what they need at the moment they need it instead of every visitor paying for a cache they
 * will never use. Verified in the hook, not assumed.
 *
 * A returning admin already has a token in localStorage, so this still goes out in parallel with the
 * first paint; on a cold start the admin's own flags are not readable yet, which is why the
 * post-auth call below stays.
 */
const warmIndexesForAdmins = () => {
  if (!isAdministrator() || !getAdminMode()) return;
  prefetchIndexes();
};
warmIndexesForAdmins();
void firebaseReady.then((user) => {
  if (user) warmIndexesForAdmins();
});

const container = document.getElementById("root")!;

// The production build prerenders the public routes (scripts/prerender.mjs writes
// the fully rendered page into dist/<route>/index.html) so crawlers get the static
// content. That snapshot is NOT hydration-compatible with this app: it captures
// POST-mount DOM (ECharts writes an imperative <canvas> + `_echarts_instance_`
// attribute into chart divs during the prerender's render delay; antd/framer-motion
// add runtime styles too). React sees server DOM that a clean client render does
// not produce, abandons hydration, and APPENDS a second full copy of the page —
// leaving the static copy (blank charts) on top and a huge empty gap before the
// duplicate. So clear the snapshot and render exactly one client copy. The static
// HTML is still served for SEO; real users get the JS-rendered page.
container.replaceChildren();
createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
